import React, { useEffect, useState } from 'react';
import { Box, Typography, Grid, Paper } from '@mui/material';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import PeopleIcon from '@mui/icons-material/People';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CampaignIcon from '@mui/icons-material/Campaign';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

interface DashboardStats {
    totalCoupons: number;
    activeCustomers: number;
    redemptionsThisMonth: number;
    activeCampaigns: number;
}

interface RecentActivity {
    _id: string;
    redeemedAt: string;
    customerName: string;
    customerEmail: string;
    timeAgo: string;
    couponId?: {
        code: string;
        title: string;
    };
}

interface DashboardData {
    stats: DashboardStats;
    recentActivity: RecentActivity[];
}

const AdminDashboard: React.FC = () => {
    const { user } = useAuth();
    const [dashboardData, setDashboardData] = useState<DashboardData>({
        stats: {
            totalCoupons: 0,
            activeCustomers: 0,
            redemptionsThisMonth: 0,
            activeCampaigns: 0
        },
        recentActivity: []
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const fetchDashboardData = async () => {
            try {
                setLoading(true);
                const response = await api.get('/admin/dashboard');
                console.log('Dashboard response:', response.data);
                setDashboardData(response.data);
            } catch (error) {
                console.error('Error fetching dashboard data:', error);
                setError('Failed to fetch dashboard data');
            } finally {
                setLoading(false);
            }
        };

        fetchDashboardData();
    }, []);

    if (loading) {
        return <Typography>Loading...</Typography>;
    }

    const { stats, recentActivity } = dashboardData;

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" sx={{ mb: 4 }}>
                Welcome back, {user?.name || 'Admin'}!
            </Typography>

            <Grid container spacing={3}>
                <Grid item xs={12} md={3}>
                    <Paper sx={{ p: 3, height: '100%', bgcolor: 'background.paper' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <LocalOfferIcon sx={{ mr: 1, color: 'primary.main' }} />
                            <Typography variant="subtitle2" color="textSecondary">
                                Total Coupons
                            </Typography>
                        </Box>
                        <Typography variant="h4">{stats?.totalCoupons}</Typography>
                    </Paper>
                </Grid>
                <Grid item xs={12} md={3}>
                    <Paper sx={{ p: 3, height: '100%', bgcolor: 'background.paper' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <PeopleIcon sx={{ mr: 1, color: 'primary.main' }} />
                            <Typography variant="subtitle2" color="textSecondary">
                                Registered Clients
                            </Typography>
                        </Box>
                        <Typography variant="h4">{stats?.activeCustomers}</Typography>
                    </Paper>
                </Grid>
                <Grid item xs={12} md={3}>
                    <Paper sx={{ p: 3, height: '100%', bgcolor: 'background.paper' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <CalendarMonthIcon sx={{ mr: 1, color: 'primary.main' }} />
                            <Typography variant="subtitle2" color="textSecondary">
                                Redemptions This Month
                            </Typography>
                        </Box>
                        <Typography variant="h4">{stats?.redemptionsThisMonth}</Typography>
                    </Paper>
                </Grid>
                <Grid item xs={12} md={3}>
                    <Paper sx={{ p: 3, height: '100%', bgcolor: 'background.paper' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <CampaignIcon sx={{ mr: 1, color: 'primary.main' }} />
                            <Typography variant="subtitle2" color="textSecondary">
                                Active Campaigns
                            </Typography>
                        </Box>
                        <Typography variant="h4">{stats?.activeCampaigns}</Typography>
                    </Paper>
                </Grid>
            </Grid>

            <Box sx={{ mt: 4 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                    Recent Activity
                </Typography>
                <Paper sx={{ p: 2, bgcolor: 'background.paper' }}>
                    {recentActivity?.map((activity) => (
                        <Box key={activity._id} sx={{ mb: 2, p: 2, borderBottom: '1px solid #eee' }}>
                            <Typography variant="caption" color="textSecondary" display="block">
                                {activity.timeAgo}
                            </Typography>
                            <Typography>
                                <strong>{activity.customerName}</strong> redeemed coupon{' '}
                                <strong>{activity.couponId?.code || 'Unknown Coupon'}</strong>
                                {activity.couponId?.title && ` - ${activity.couponId.title}`}
                            </Typography>
                        </Box>
                    ))}
                    {recentActivity?.length === 0 && (
                        <Typography color="textSecondary" align="center" sx={{ py: 2 }}>
                            No recent activity
                        </Typography>
                    )}
                </Paper>
            </Box>
        </Box>
    );
};

export default AdminDashboard;
